import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type FunctionComponent,
  type ReactNode,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { matchPath, useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import {
  NavigationBar,
  TOPBAR_HEIGHT,
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
  useSidebarContext,
  useTheme,
} from '@retailer-platform/shared-components/src/tds'
import { useCoreContext } from '../../../utils/core/RPPCoreContext'
import { useAbsoluteRouteMatch } from '../../../utils/routing/useAbsoluteRouteMatch.hooks'
import { useTrackEvent } from '../../../utils/events/useTrackEvent.hook'
import { url as resolveUrlPattern } from '../../../utils/parsing/url'
import { toSentenceCase } from '../../../utils/strings/toSentenceCase'
import icPlatformLogo from '../nav-v2/assets/ic-platform.svg'
import { type NavMenuHierarchyL1, type NavMenuHierarchyL2 } from '../nav-v2/NavMenuHierarchy'
import { useOptionalPartnerContext } from '../../../exports/utils'
import { type NavSupportedEnvironment } from '../../../legacy/components/Nav/nav.types'
import { getHrefOverride } from './navEnvironment'
import { NavVersionSwitcherButton } from './NavVersionSwitcherButton'
import { TdsAppSwitcher } from './TdsAppSwitcher'
import { TdsHelpMenu } from './TdsHelpMenu'
import { TdsNotificationsButton } from './TdsNotificationsButton'
import { TdsPartnerMenu } from './TdsPartnerMenu'
import { TdsSearchButton } from './TdsSearchButton'
import { TopBarMenuProvider } from './TopBarMenuContext'
import { type NavMenuHierarchyV3 } from './navSectionV3'
import { getNavigationActionEventDataV3 } from './navV3.tracking'
import { useV3SearchActions } from './useV3SearchActions'

type Props = {
  navMenuHierarchy: NavMenuHierarchyV3
  isCollapsed: boolean
  onCollapsedChange: (next: boolean) => void
  // Route name to navigate to when the top-left logo is clicked. Resolved via
  // the same useResolveNavHref hook used by NavItems, so active-route params
  // (e.g. :partner_id, :warehouse_id) are substituted from the current URL.
  homeRoute: string
  // Optional override for the TopBar's right cluster. When omitted, the default
  // partner-scoped cluster (Notifications + Search + Help + Partner) renders.
  // Admin sections that lack PartnerContext should pass a partner-safe cluster here.
  topBarRight?: ReactNode
  // When true, the L1 sidebar is omitted entirely — only the TopBar renders.
  // Used by the v3 shell on settings routes where the L2 settings rail replaces
  // the L1 rail as the page's primary navigation.
  hideSidebar?: boolean
}

const DefaultRetailerTopBarRight: FunctionComponent = () => {
  const actions = useV3SearchActions()
  return (
    <>
      <NavVersionSwitcherButton target="v2" />
      <TdsNotificationsButton />
      <TdsSearchButton actions={actions} />
      <TdsHelpMenu />
      <TdsPartnerMenu />
    </>
  )
}

type ResolvedNavHref = { href: string; isExternal: boolean } | undefined

type Navigable = {
  href?: string
  route?: string
  newTab?: boolean
  subRoutes?: string[]
  environment?: NavSupportedEnvironment
}

export const useResolveNavHref = (item: Navigable): ResolvedNavHref => {
  const { routesByName } = useCoreContext()
  const routeMatch = useAbsoluteRouteMatch()
  const partnerContext = useOptionalPartnerContext()

  return useMemo(() => {
    if (item.href) return { href: item.href, isExternal: true }
    if (!item.route) return undefined

    const override = getHrefOverride(
      item.route,
      // @ts-expect-error TS2345 — strictNullChecks. TODO: fix strict null check
      item.environment,
      partnerContext,
      routesByName,
      (routeMatch?.params as { warehouse_id?: string })?.warehouse_id
    )
    if (override) return { href: override, isExternal: true }

    const pattern = routesByName[item.route]
    if (!pattern) return undefined

    return {
      href: resolveUrlPattern(pattern, (routeMatch?.params as Record<string, string>) ?? {}),
      isExternal: Boolean(item.newTab),
    }
  }, [
    item.href,
    item.route,
    item.newTab,
    item.environment,
    partnerContext,
    routesByName,
    routeMatch?.params,
  ])
}

// Number of literal (non-parameter) segments in a route pattern. Used to
// rank patterns by specificity so a wildcard match can be suppressed when a
// concrete sibling pattern also matches the same URL.
const countLiteralSegments = (pattern: string): number =>
  pattern.split('/').filter(seg => seg && !seg.startsWith(':')).length

// True when some other registered pattern matches `pathname` exactly AND has
// strictly more literal segments than `pattern`. When that happens, the
// more-specific pattern is the URL's "real" owner (this is also how
// react-router itself picks a winner when multiple <Route>s match) and the
// less-specific wildcard match here is a false positive.
//
// Concrete example — the bug this exists to prevent:
//
//   In Storefront Pro's sub-nav, "Campaigns" lists `partner-campaigns-view`
//   (pattern `/partners/:partnerId/campaigns/:campaignId`) as a subRoute so
//   that drilling into a specific campaign keeps the Campaigns item lit up.
//   "Automated campaigns" sits next to it with route
//   `/partners/:partnerId/campaigns/lifecycle-crm`.
//
//   On `/partners/123/campaigns/lifecycle-crm`, the wildcard subRoute matches
//   exactly with `:campaignId="lifecycle-crm"`, so without this check both
//   nav items would highlight. `exact: true` doesn't disambiguate — both
//   patterns are exactly three segments.
//
//   `isOutmatchedBy` notices that the sibling `…/campaigns/lifecycle-crm`
//   pattern has 2 literal segments vs. the wildcard's 1, also matches the
//   URL, and so the sibling wins. The wildcard match is suppressed and only
//   "Automated campaigns" highlights.
const isOutmatchedBy = (
  pattern: string,
  pathname: string,
  routesByName: Record<string, string>
): boolean => {
  const ourSpecificity = countLiteralSegments(pattern)
  for (const candidate of Object.values(routesByName)) {
    if (candidate === pattern) continue
    if (countLiteralSegments(candidate) <= ourSpecificity) continue
    if (matchPath(pathname, { path: candidate, exact: true })) return true
  }
  return false
}

// Pure version of the active check, broken out so callers that walk a tree of
// items can evaluate every node against a single pathname/routesByName pair
// without violating the rules of hooks.
//
// `exact` opts into react-router's exact match for the primary route. Use it
// for "leaf" L1s whose route is a landing page (e.g. admin Home → `/admin`) —
// without exact, every URL that extends the landing path would keep the L1
// highlighted. subRoutes are always matched exactly regardless of `exact`
// (see below).
const isNavItemActiveAt = (
  item: Navigable,
  pathname: string,
  routesByName: Record<string, string>,
  exact = false
): boolean => {
  if (item.href) return false

  if (item.route) {
    const pattern = routesByName[item.route]
    if (
      pattern &&
      matchPath(pathname, { path: pattern, exact }) &&
      !isOutmatchedBy(pattern, pathname, routesByName)
    ) {
      return true
    }
  }

  // subRoutes are an EXPLICIT enumeration of sibling pages that should keep
  // this nav item highlighted. Always match exactly to avoid prefix bleed
  // (a wildcard like `/campaigns/:campaignId` would otherwise prefix-match
  // `/campaigns/storefront-pro/list`). The `isOutmatchedBy` check then
  // suppresses wildcard matches that a more-specific concrete sibling
  // pattern also satisfies — see that helper for the worked example.
  for (const subRouteName of item.subRoutes ?? []) {
    const pattern = routesByName[subRouteName]
    if (!pattern) continue
    if (!matchPath(pathname, { path: pattern, exact: true })) continue
    if (isOutmatchedBy(pattern, pathname, routesByName)) continue
    return true
  }

  return false
}

// Active when the current URL matches the item's route pattern (prefix match
// — react-router's default for matchPath). Also matches against any `subRoutes`
// declared on the item, looked up the same way by route name. This is how nav
// entries claim selection on sibling URLs that don't share a path prefix
// (e.g. a list at `…/list` staying highlighted on `…/create/:id`).
// External-href items can't be active.
export const useIsNavItemActive = (item: Navigable, exact = false): boolean => {
  const { routesByName } = useCoreContext()
  const { pathname } = useLocation()
  return isNavItemActiveAt(item, pathname, routesByName, exact)
}

// True when any leaf descendant of `l1` is active for the current URL. Used by
// the expandable L1 to decide both icon variant (filled when any child is the
// current page) and initial expansion (auto-open the L1 that contains the
// active page). Walks `children` recursively so it works for both flat L1→L2
// shapes and `hasL2Children` shapes (L1→L2-section→L3).
const useIsAnyDescendantActive = (l1: NavMenuHierarchyL1): boolean => {
  const { routesByName } = useCoreContext()
  const { pathname } = useLocation()
  return useMemo(() => {
    const walk = (node: NavMenuHierarchyL1 | NavMenuHierarchyL2): boolean => {
      const kids = (node as NavMenuHierarchyL2).children
      if (kids && kids.length > 0) return kids.some(walk)
      return isNavItemActiveAt(node, pathname, routesByName)
    }
    return (l1.children ?? []).some(walk)
  }, [l1, pathname, routesByName])
}

// TDS's NavGroupTrigger triggerActive style only renders a thin
// brandPrimaryRegular strip and no background. To match the L2
// selected look (light green background + dark green strip), override
// the active styling locally via the data-active attribute that TDS
// sets on the trigger element. Used by both the leaf-L1 path and the
// expandable-L1 path so the two render the same selected pill.
const useTriggerSelectedOverride = () => {
  const theme = useTheme()
  return {
    '& [data-active="true"]': {
      backgroundColor: theme.colors.brandPrimaryExtraLight,
      boxShadow: `inset 4px 0 0 ${theme.colors.brandPrimaryDark}`,
      color: theme.colors.brandPrimaryDark,
    },
  }
}

// TDS's NavigationBar.NavDivider wraps a 16px-tall Divider in a 40px row,
// which is too generous vs. the Figma spec (file LBXH9MrgHmviUPM0x3DKKx,
// node 11289-163297, ~24px total). Reimplemented inline so the line is
// rendered directly with symmetric padding — using TDS's <Divider> here
// surfaces a margin-collapse quirk that pushes the line to the top of the
// 16px container, making it look off-center between adjacent NavItems.
// Horizontal padding matches TDS NavItem (pl 60 / pr 16).
const TdsNavGroupDivider: FunctionComponent = () => {
  const theme = useTheme()
  return (
    <li
      role="separator"
      data-testid="NavDivider"
      style={{
        listStyle: 'none',
        paddingTop: 12,
        paddingBottom: 12,
        paddingLeft: 60,
        paddingRight: 16,
      }}
    >
      <div style={{ height: 1, backgroundColor: theme.colors.systemGrayscale20 }} />
    </li>
  )
}

const TdsNavLeaf: FunctionComponent<{ l2: NavMenuHierarchyL2 }> = ({ l2 }) => {
  const resolved = useResolveNavHref(l2)
  const isActive = useIsNavItemActive(l2)
  const trackEvent = useTrackEvent()
  return (
    <NavigationBar.NavItem
      href={resolved?.href}
      isExternal={resolved?.isExternal ?? false}
      isActive={isActive}
      onPress={() => trackEvent(getNavigationActionEventDataV3(resolved?.href, l2.route))}
    >
      <FormattedMessage id={l2.name}>
        {(...nodes) => toSentenceCase(String(nodes[0] ?? ''))}
      </FormattedMessage>
    </NavigationBar.NavItem>
  )
}

// Non-expandable L1: a NavGroup with only a NavGroupTrigger (no NavGroupContent)
// — TDS suppresses the chevron and routes clicks via the trigger's href instead
// of toggling expansion (NavGroup's `hasContent` stays false).
//
// Uses exact matching so a leaf L1 whose route is a landing page (e.g. admin
// Home → `/admin`) doesn't stay highlighted on every URL that extends its path.
const TdsLeafL1: FunctionComponent<{ l1: NavMenuHierarchyL1 }> = ({ l1 }) => {
  const resolved = useResolveNavHref(l1)
  const isActive = useIsNavItemActive(l1, true)
  const trackEvent = useTrackEvent()
  const triggerSelectedOverride = useTriggerSelectedOverride()
  const L1Icon = l1.icon
  // Tag the Home L1 so the nav-v3 onboarding tour can anchor its first step
  // directly to it. Both retailer and admin nav hierarchies use this name.
  const tourId = l1.name === 'navV3.folder.home.title' ? 'home' : undefined
  return (
    <NavigationBar.NavGroup>
      <span
        css={triggerSelectedOverride}
        style={{ display: 'block' }}
        data-onboarding-tour-id={tourId}
      >
        <NavigationBar.NavGroupTrigger
          href={resolved?.href}
          icon={<L1Icon size={20} isActive={isActive} />}
          isActive={isActive}
          onPress={() => trackEvent(getNavigationActionEventDataV3(resolved?.href, l1.route))}
        >
          <FormattedMessage id={l1.name} />
        </NavigationBar.NavGroupTrigger>
      </span>
    </NavigationBar.NavGroup>
  )
}

// Expandable L1: a NavGroup with both a trigger and a content panel. Drives
// two derived states off "is any descendant active":
//   - icon variant flips outline → filled (matches Figma 10911:107072)
//   - the panel auto-expands when a descendant becomes active (cold load to a
//     deep route, or SPA nav into one). Local expanded state is preserved
//     across renders so the user can still collapse/expand manually; we only
//     force-expand on the rising edge of `hasActiveDescendant`.
const TdsExpandableL1: FunctionComponent<{ l1: NavMenuHierarchyL1 }> = ({ l1 }) => {
  const intl = useIntl()
  const { isCollapsed, isOverlayOpen } = useSidebarContext()
  const visuallyCollapsed = isCollapsed && !isOverlayOpen
  const hasActiveDescendant = useIsAnyDescendantActive(l1)
  const [expanded, setExpanded] = useState(hasActiveDescendant)
  useEffect(() => {
    if (hasActiveDescendant) setExpanded(true)
  }, [hasActiveDescendant])
  const triggerSelectedOverride = useTriggerSelectedOverride()
  const L1Icon = l1.icon
  // Highlight the L1 itself when an active descendant exists but isn't
  // currently visible — i.e. the L1 group is locally collapsed, OR the
  // sidebar is in icon-only mode (matches TDS's own visuallyCollapsed
  // predicate in NavGroupContent.js:20-24, which hides the children in
  // both cases).
  const showAsSelected = hasActiveDescendant && (!expanded || visuallyCollapsed)
  return (
    <NavigationBar.NavGroup isExpanded={expanded} onExpandedChange={setExpanded}>
      <span css={triggerSelectedOverride} style={{ display: 'block' }}>
        <NavigationBar.NavGroupTrigger
          icon={<L1Icon size={20} isActive={hasActiveDescendant} />}
          isActive={showAsSelected}
        >
          <FormattedMessage id={l1.name} />
        </NavigationBar.NavGroupTrigger>
      </span>
      <NavigationBar.NavGroupContent>
        {l1.hasL2Children
          ? l1.children.map((l2, j) => (
              <Fragment key={`${l2.name}-${j}`}>
                <NavigationBar.NavSection
                  title={toSentenceCase(intl.formatMessage({ id: l2.name }))}
                />
                {(l2.children ?? []).map((l3, k) => (
                  <TdsNavLeaf key={`${l3.name}-${k}`} l2={l3} />
                ))}
              </Fragment>
            ))
          : l1.children.map((l2, j) => (
              <Fragment key={`${l2.name}-${j}`}>
                {l2.dividerBefore && j > 0 && <TdsNavGroupDivider />}
                <TdsNavLeaf l2={l2} />
              </Fragment>
            ))}
      </NavigationBar.NavGroupContent>
    </NavigationBar.NavGroup>
  )
}

export const TdsNavigationBar: FunctionComponent<Props> = ({
  navMenuHierarchy,
  isCollapsed,
  onCollapsedChange,
  homeRoute,
  topBarRight,
  hideSidebar,
}) => {
  const intl = useIntl()
  const homeHref = useResolveNavHref({ route: homeRoute })?.href
  const trackEvent = useTrackEvent()
  return (
    <NavigationBar>
      <NavigationBar.TopBar>
        <TopBarMenuProvider>
          <NavigationBar.TopBarLeft>
            <TdsAppSwitcher homeRoute={homeRoute} />
            {homeHref ? (
              <Link
                to={homeHref}
                aria-label="Instacart Platform home"
                style={{ display: 'inline-flex' }}
                onClick={() => trackEvent(getNavigationActionEventDataV3(homeHref, homeRoute))}
              >
                <img
                  src={icPlatformLogo}
                  alt="Instacart Platform"
                  style={{ height: 18, display: 'block' }}
                />
              </Link>
            ) : (
              <img
                src={icPlatformLogo}
                alt="Instacart Platform"
                style={{ height: 18, display: 'block' }}
              />
            )}
          </NavigationBar.TopBarLeft>
          <NavigationBar.TopBarRight>
            {topBarRight ?? <DefaultRetailerTopBarRight />}
          </NavigationBar.TopBarRight>
        </TopBarMenuProvider>
      </NavigationBar.TopBar>
      {!hideSidebar && (
        <NavigationBar.Sidebar isCollapsed={isCollapsed} onCollapsedChange={onCollapsedChange}>
          <NavigationBar.SidebarContent aria-label="Primary navigation">
            {navMenuHierarchy.sections.map(section => (
              <Fragment key={section.id}>
                {section.titleId && (
                  <NavigationBar.NavSection title={intl.formatMessage({ id: section.titleId })} />
                )}
                {section.l1s.map((l1, l1Idx) => {
                  if (l1.route || l1.href) {
                    return <TdsLeafL1 key={`${l1.name}-${l1Idx}`} l1={l1} />
                  }
                  return <TdsExpandableL1 key={`${l1.name}-${l1Idx}`} l1={l1} />
                })}
              </Fragment>
            ))}
          </NavigationBar.SidebarContent>
          {/* `display: contents` wrapper tags the footer for the onboarding tour
           * without breaking the sidebar's flex layout — TDS NavigationBar.SidebarFooter
           * doesn't accept arbitrary data attrs. `useAnchoredPosition` descends to
           * the first sized child when computing the popup placement. */}
          <div data-onboarding-tour-id="sidebar-collapse" style={{ display: 'contents' }}>
            <NavigationBar.SidebarFooter />
          </div>
        </NavigationBar.Sidebar>
      )}
    </NavigationBar>
  )
}

// Re-exported so consumers building layout around the NavigationBar (e.g. sizing the
// content area to the right of the Sidebar and below the TopBar) can use the canonical
// TDS constants instead of hardcoding them.
export { TOPBAR_HEIGHT, SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH }
